/**
 * HomePage Component
 *
 * Professional landing page focusing on:
 * - Evidence as infrastructure
 * - Legal/enterprise positioning
 * - Risk reduction, not technology hype
 *
 * NO Web3/blockchain branding visible to users
 */

import React from 'react';
import { Link } from 'react-router-dom';
import './HomePage.css';

function HomePage() {
  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="hero">
        <div className="container">
          <h1>Evidence Infrastructure<br />for Legal Systems</h1>
          <p className="hero-subtitle">
            Verified. Secure. Court-Admissible.
          </p>
          <p className="hero-description">
            Professional evidence verification that makes legal proceedings safer to trust.
            The notary that never sleeps.
          </p>
          <div className="hero-actions">
            <Link to="/evidence" className="btn btn-primary btn-large">
              Upload Evidence
            </Link>
            <Link to="/verify" className="btn btn-secondary btn-large">
              Verify Evidence
            </Link>
          </div>

          {/* Trust Indicators */}
          <div className="trust-indicators">
            <div className="trust-item">
              <span className="trust-icon">✓</span>
              <span>Court Admissible</span>
            </div>
            <div className="trust-item">
              <span className="trust-icon">🔒</span>
              <span>Tamper-Evident</span>
            </div>
            <div className="trust-item">
              <span className="trust-icon">⚡</span>
              <span>Instant Verification</span>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Statement */}
      <section className="section problem-section">
        <div className="container">
          <div className="section-title">
            <h2>What Problem Breaks If Evidencer Disappears?</h2>
            <p>
              Evidence integrity. Chain of custody. Legal admissibility.
              The stakes are too high for manual processes.
            </p>
          </div>

          <div className="problem-grid">
            <div className="problem-card">
              <h3>Hostile Discovery</h3>
              <p>
                Opposing counsel challenges evidence authenticity.
                Without cryptographic verification, your evidence is vulnerable.
              </p>
            </div>
            <div className="problem-card">
             <h3>Media Alteration Claims</h3>
              <p>
                Photos and videos are easily manipulated.
                Courts need proof that evidence hasn't been altered.
              </p>
            </div>
            <div className="problem-card">
              <h3>Chain of Custody Gaps</h3>
              <p>
                Manual custody logs can be lost or forged.
                One break in the chain can invalidate critical evidence.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Solution - How It Works */}
      <section className="section how-it-works">
        <div className="container">
          <div className="section-title">
            <h2>How Evidencer Works</h2>
            <p>Simple for users. Unbreakable for adversaries.</p>
          </div>

          <div className="steps-grid">
            <div className="step-card">
              <div className="step-number">1</div>
              <h3>Upload Evidence</h3>
              <p>
                Drag and drop files. Evidence is instantly fingerprinted
                using cryptographic hashing.
              </p>
            </div>
            <div className="step-card">
              <div className="step-number">2</div>
              <h3>Automatic Verification</h3>
              <p>
                Evidence is secured in tamper-evident storage with
                immutable timestamp verification.
              </p>
            </div>
            <div className="step-card">
              <div className="step-number">3</div>
              <h3>Get Certificate</h3>
              <p>
                Receive court-admissible verification certificate.
                Anyone can independently verify authenticity.
              </p>
            </div>
          </div>

          <div className="cta-inline">
            <Link to="/use-cases" className="btn btn-primary">
              View Detailed Scenarios →
            </Link>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="section benefits-section">
        <div className="container">
          <div className="section-title">
            <h2>What Makes Systems Safer to Trust</h2>
          </div>

          <div className="benefits-grid">
            <div className="benefit-card">
              <h3>Compliance Drag Reduction</h3>
              <p>
                Automated evidence verification reduces manual review time
                by 80%. Less time on chain of custody documentation.
              </p>
              <div className="benefit-stat">
                <span className="stat-number">80%</span>
                <span className="stat-label">Time Saved</span>
              </div>
            </div>

            <div className="benefit-card">
              <h3>Evidentiary Defensibility</h3>
              <p>
                Cryptographic proof of authenticity withstands adversarial
                scrutiny. Meets Federal Rules of Evidence requirements.
              </p>
              <div className="benefit-stat">
                <span className="stat-number">100%</span>
                <span className="stat-label">Admissibility Rate</span>
              </div>
            </div>


            <div className="benefit-card">
              <h3>Risk Category Removal</h3>
              <p>
                Eliminate entire classes of evidentiary risks. Sleep
                better knowing evidence integrity is mathematically guaranteed.
              </p>
              <div className="benefit-stat">
                <span className="stat-number">Zero</span>
                <span className="stat-label">Alterations Possible</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases Preview */}
      <section className="section use-cases-preview">
        <div className="container">
          <div className="section-title">
            <h2>Three Situations That Make Lawyers Uncomfortable</h2>
            <p>Real scenarios. Real risks. Real solutions.</p>
          </div>

          <div className="use-cases-grid">
            <div className="use-case-card">
              <div className="use-case-number">01</div>
              <h3>Evidence That Survives Hostile Discovery</h3>
              <p>
                Opposing counsel aggressively challenges every piece of evidence.
                Your verification withstands all scrutiny.
              </p>
              <Link to="/use-cases#hostile-discovery" className="use-case-link">
                View Scenario →
              </Link>
            </div>

            <div className="use-case-card">
              <div className="use-case-number">02</div>
              <h3>Media That Disproves Alteration Claims</h3>
              <p>
                Defense claims your video evidence was manipulated.
                Cryptographic fingerprints prove otherwise.
              </p>
              <Link to="/use-cases#media-integrity" className="use-case-link">
                View Scenario →
              </Link>
            </div>

            <div className="use-case-card">
              <div className="use-case-number">03</div>
              <h3>Chain-of-Custody No Human Can Fake</h3>
              <p>
                Multiple parties handle evidence. Every transfer is
                cryptographically recorded and verified.
              </p>
              <Link to="/use-cases#chain-of-custody" className="use-case-link">
                View Scenario →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Enterprise CTA */}
      <section className="enterprise-cta">
        <div className="container">
          <div className="enterprise-cta-content">
            <h2>Enterprise Evidence Infrastructure</h2>
            <p>
              For law firms, insurance companies, and corporate legal departments
              who can't afford evidence integrity failures.
            </p>
            <div className="enterprise-features">
              <div className="enterprise-feature">
                <span className="feature-check">✓</span>
                <span>Unlimited evidence storage</span>
              </div>
              <div className="enterprise-feature">
                <span className="feature-check">✓</span>
                <span>Custom compliance reports</span>
              </div>
              <div className="enterprise-feature">
                <span className="feature-check">✓</span>
                <span>Priority verification</span>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              <a
                href="https://enterprise.evidencer.io/pricing"
                className="btn btn-primary btn-large"
                rel="noopener noreferrer"
              >
                View Pricing →
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="section final-cta">
        <div className="container">
          <h2>Start Verifying Evidence Today</h2>
          <p>
            Upload evidence in seconds. Get court-admissible verification certificates.
            No complex setup. No technical knowledge required.
          </p>
          <div className="final-cta-actions">
            <Link to="/evidence" className="btn btn-primary btn-large">
              Upload Evidence
            </Link>
            <Link to="/verify" className="btn btn-secondary btn-large">
              Verify Existing Evidence
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default HomePage;
